'use client';

import React, { useEffect, useState } from 'react';
import { type Room } from './gameData';
import { playGameOverSound } from './GameScreen';

interface JumpscareOverlayProps {
  room: Room;
  onRestart: () => void;
}

export default function JumpscareOverlay({ room, onRestart }: JumpscareOverlayProps) {
  const [phase, setPhase] = useState<'white' | 'image' | 'shake' | 'settle' | 'restart'>('white');

  useEffect(() => {
    // Play the scary cinematic game over sound
    playGameOverSound();

    // Phase timeline: white flash → image zoom → shake → settle → show restart
    const t1 = setTimeout(() => setPhase('image'), 60);
    const t2 = setTimeout(() => setPhase('shake'), 80);
    const t3 = setTimeout(() => setPhase('settle'), 600);
    const t4 = setTimeout(() => setPhase('restart'), 1800);

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ cursor: phase === 'restart' ? 'pointer' : 'default' }}
      onClick={phase === 'restart' ? onRestart : undefined}
    >
      {/* White flash */}
      {phase === 'white' && (
        <div className="absolute inset-0 bg-white z-20" />
      )}

      {/* Full-screen horror image — zooms in aggressively */}
      <div
        className="absolute inset-0"
        style={{
          transform: phase === 'image' || phase === 'shake' ?'scale(1.25)'
            : phase === 'settle'|| phase === 'restart' ?'scale(1.05)' :'scale(2)',
          transition: phase === 'settle' || phase === 'restart' ? 'transform 0.8s ease-out' : 'transform 0.08s ease-out',
        }}
      >
        <img
          src={room.jumpscare.imageUrl}
          alt="Jumpscare"
          className="w-full h-full object-cover"
          style={{
            filter: 'brightness(0.5) saturate(0.3) contrast(2) hue-rotate(340deg)',
          }}
        />
      </div>

      {/* Red overlay — flashes then settles */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background: phase === 'white' || phase === 'image' || phase === 'shake' ?'rgba(180,0,0,0.55)' :'rgba(80,0,0,0.65)',
          transition: 'background 0.4s ease',
          mixBlendMode: 'multiply',
        }}
      />

      {/* Scanlines */}
      <div className="scanlines absolute inset-0 z-10 pointer-events-none" aria-hidden="true" />

      {/* Vignette */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.95) 100%)' }}
      />

      {/* Screen shake overlay */}
      {(phase === 'shake') && (
        <div className="absolute inset-0 z-20 pointer-events-none shake-screen" />
      )}

      {/* GAME OVER text — appears after settle, no explanation */}
      {(phase === 'settle' || phase === 'restart') && (
        <div
          className="absolute inset-0 z-30 flex flex-col items-center justify-center px-6"
          style={{
            animation: 'fade-in-horror 0.5s ease forwards',
          }}
        >
          <h1
            className="font-horror text-6xl sm:text-8xl tracking-widest text-white mb-4"
            style={{
              textShadow: '0 0 40px rgba(255,0,0,1), 0 0 80px rgba(255,0,0,0.7), 0 0 120px rgba(255,0,0,0.4)',
              animation: 'flicker 0.4s ease-in-out infinite',
            }}
          >
            GAME OVER
          </h1>

          {phase === 'restart' && (
            <button
              onClick={onRestart}
              className="restart-btn text-lg sm:text-xl px-8 py-4 rounded-lg tracking-widest uppercase mt-6 cursor-pointer"
              style={{ minWidth: '220px', fontFamily: '"Oswald", sans-serif', fontWeight: 700 }}
              aria-label="Pradėti iš naujo"
            >
              Pradėti iš naujo
            </button>
          )}
        </div>
      )}
    </div>
  );
}